import { FieldPolicy, InMemoryCache } from "@apollo/client/cache";
import { ApolloClient, ApolloLink } from "@apollo/client/core";
import { SetContextLink } from "@apollo/client/link/context";
import { HttpLink } from "@apollo/client/link/http";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import { createClient } from "graphql-ws";

/**
 * Create an Apollo Client configured for HTTP queries/mutations and WS subscriptions.
 * @param apiUrl - Base URL of the GraphQL endpoint (http/https). A WS URL is derived for subscriptions.
 * @param accessTokenProvider - Optional function returning a Bearer token (string or Promise<string>).
 * @returns Apollo Client instance.
 * @example
 * import { createApolloClient } from "@keyban/sdk-base/dist/apollo";
 *
 * const apollo = createApolloClient(new URL("https://subql.example.com/graphql"), async () => getAccessToken());
 * const result = await apollo.query({ query: MY_QUERY });
 */
export function createApolloClient(
  apiUrl: URL,
  accessTokenProvider?: () => string | Promise<string>,
) {
  const authLink = new SetContextLink(async ({ headers }) => {
    const token = await accessTokenProvider?.();

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const httpLink = new HttpLink({ uri: apiUrl.toString() });

  const wsUrl = new URL(apiUrl);
  wsUrl.protocol = "wss";
  const wsLink = new GraphQLWsLink(createClient({ url: wsUrl.toString() }));

  const transportLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink,
  );

  const dateFieldPolicy: FieldPolicy<string> = {
    read: (value) => value && value + "Z",
  };

  return new ApolloClient({
    devtools: { enabled: true },
    defaultOptions: {
      query: { fetchPolicy: "network-only" },
      watchQuery: { fetchPolicy: "cache-and-network" },
    },
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            tokenBalances: relayStylePagination(["filter"]),
            nftBalances: relayStylePagination(["filter"]),
            assetTransfers: relayStylePagination(["filter"]),
            orders: relayStylePagination(["filter"]),
          },
        },
        Transaction: {
          fields: { date: dateFieldPolicy },
        },
        Order: {
          fields: { createdAt: dateFieldPolicy },
        },
      },
    }),
    link: ApolloLink.from([authLink, transportLink]),
  });
}
